import React, { useState, useEffect, useContext, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../redux/store';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import { filterDocuments, getDocsForSlice, getSlices, saveMultipleDocuments } from '../../../../common/services/http/documentsService';
import { DocumentSliceHierarchy } from './DocumentSliceHierarchy';
import { TreeList, mapTree, extendDataItem, TreeListColumnProps, TreeListSortChangeEvent } from '@progress/kendo-react-treelist';
import { DocumentSliceNode } from './DocumentSliceNode';
import FilterCtx from '../../../../context/filterContext/filterCtx';
import DocsCtx from '../../../../context/docsContext/docsCtx';
import HeaderCell from '../cells/headerCell/headerCell';
import DateCell from '../cells/dateCell/dateCell';
import ChipsCell from '../cells/chipsCell/chipsCell';
import ReadCell from '../cells/readCell/readCell';
import ActionsCell from '../cells/actionsCell/actionsCell';
import DocumentNameCell from '../cells/documentNameCell/documentNameCell';
import DocumentFilters from '../filters/documentFilters';
import TableLoading from '../../../ui/loaders/tableLoader/tableLoading';
import useTranslate from '../../../../common/hooks/useTranslate';
import TextCell from '../../../ui/table/cells/textCell/textCell';
import TreeListCell from '../cells/treelistCell/treeListCell';
import getPeriodRange from '../../../../common/utility/getPeriodRange';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import removeFirstItemFromArray from '../../../../common/utility/removeFirstItemFromArray';
import DocNameFilter from '../../../ui/filters/docNameFilter/docNameFilter';
import MobileNameCell from '../cells/mobileNameCell/mobileNameCell';

import { REACHED_LIMIT_MODAL, SWITCH_MULTIPLE_DOWNLOAD_MODAL } from '../../../../redux/types';

import '@progress/kendo-theme-default/dist/all.css';
import './documentsTable.scss';
import '../../../../common/styles/globalClasses.scss';

interface ISort {
  field: string;
  dir: 'asc' | 'desc';
}

const initialSort: ISort[] = [{ field: 'publishDate', dir: 'desc' }];

const subItemsField: string = 'children';
const expandField: string = 'expanded';

const DocumentsTable = () => {
  const filterCtx = useContext(FilterCtx);
  const docsCtx = useContext(DocsCtx);

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [sort, setSort] = useState(initialSort);
  const [hierarchyData, setHierarchyData] = useState([]);
  const [expanded, setExpanded] = useState<any[]>([]);
  const [sliceArray, setSliceArray] = useState<string[]>([]);

  const [sliceSort, setSliceSort] = useState<SortDescriptor[]>([{ field: 'name', dir: 'asc' }]);

  const { t } = useTranslate();
  const dispatch = useDispatch();

  const isFetching = useRef(false);
  const tableRef = useRef(null);
  const docHierarchyRef = useRef(new DocumentSliceHierarchy());

  const impersonate = useSelector((state: RootState) => state.impersonate);

  const pageSize = 100;
  const take = pageSize * 3;
  const isMultipleDownloadOn = docsCtx.selectedItemsIds && docsCtx.selectedItemsIds.length > 1;
  const limitReached = docsCtx.selectedItemsDocIds && docsCtx.selectedItemsDocIds.length >= 20;

  const isDocNumberShown = expanded.length;

  const onUncheck = (docId, id) => {
    docsCtx.setSelectedItemsDocIds((prevState) => {
      return removeFirstItemFromArray(prevState, docId);
    });
    docsCtx.setSelectedItemsIds((prevState) => {
      return prevState.filter((item) => item !== id);
    });
  };

  const handleDownloadClick = () => {
    let idsArray = docsCtx.selectedItemsDocIds.map((docId) => {
      return { id: docId };
    });
    saveMultipleDocuments(idsArray)
      .then(() => {
        dispatch({
          type: SWITCH_MULTIPLE_DOWNLOAD_MODAL,
          payload: {
            text: t('DOCUMENTS_TABLE.MULTIPLE_DOWNLOAD_SUCCESSFUL'),
            error: false,
          },
        });
        docsCtx.setSelectedItemsDocIds([]);
        docsCtx.setSelectedItemsIds([]);
      })
      .catch(() => {
        dispatch({
          type: SWITCH_MULTIPLE_DOWNLOAD_MODAL,
          payload: {
            text: t('DOCUMENTS_TABLE.MULTIPLE_DOWNLOAD_ERROR'),
            error: true,
          },
        });
      });
  };

  const onCheck = (docId, id) => {
    if (limitReached) {
      dispatch({ type: REACHED_LIMIT_MODAL });
    } else {
      docsCtx.setSelectedItemsDocIds([...docsCtx.selectedItemsDocIds, docId]);
      docsCtx.setSelectedItemsIds([...docsCtx.selectedItemsIds, id]);
    }
  };

  const DocNameCell = (props) => <DocumentNameCell {...props} origin="documentsPage" />;
  const GridMobileNameCell = (props) => <MobileNameCell {...props} origin="documentsPage"/>;
  const ReadDocCell = (props) => <ReadCell {...props} />;
  const ActCell = (props) => (
    <ActionsCell
      {...props}
      docId={props.dataItem.documentId}
      id={props.dataItem.id}
      onCheck={onCheck}
      onUncheck={onUncheck}
      multipleDownload={isMultipleDownloadOn}
      onMultipleDownloadClick={handleDownloadClick}
    />
  );
  const TextCellDC = (props) => <TextCell useTooltip={true} {...props} />;

  const ReadDocCellTreelist = (props) => <ReadCell {...props} treelist />;
  const ActCellTreelist = (props) => (
    <ActionsCell
      {...props}
      isDocNumberShown={isDocNumberShown}
      docId={props.dataItem.documentId}
      id={props.dataItem.id}
      treelist
      onCheck={onCheck}
      onUncheck={onUncheck}
      multipleDownload={isMultipleDownloadOn}
      onMultipleDownloadClick={handleDownloadClick}
    />
  );
  const TextCellTreelist = (props) => <TextCell useTooltip={true} {...props} treelist />;
  const DateCellTreelist = (props) => <DateCell {...props} treelist />;
  const ChipsCellTreelist = (props) => <ChipsCell useTooltip={true} {...props} treelist />;

  const columns: TreeListColumnProps[] = [
    {
      // @ts-ignore
      field: 'label',
      title: t('DOCUMENTS_TABLE.COLUMN_NAME'),
      expandable: true,
      headerCell: HeaderCell,
      cell: TreeListCell,
    },
    {
      // @ts-ignore
      field: 'publishDate',
      title: t('DOCUMENTS_TABLE.COLUMN_POSTED'),
      headerCell: HeaderCell,
      cell: DateCellTreelist,
    },
    {
      // @ts-ignore
      field: 'classifications',
      title: t('DOCUMENTS_TABLE.COLUMN_CLASSIFICATIONS'),
      className: 'tableCell',
      headerCell: HeaderCell,
      cell: ChipsCellTreelist,
    },
    {
      // @ts-ignore
      field: 'fundName',
      title: t('DOCUMENTS_TABLE.COLUMN_FUND'),
      className: 'tableCell',
      headerCell: HeaderCell,
      cell: TextCellTreelist,
    },
    {
      // @ts-ignore
      field: 'fundInvestorName', //'objectName',
      title: t('DOCUMENTS_TABLE.COLUMN_INVESTOR'),
      className: 'tableCell',
      headerCell: HeaderCell,
      cell: TextCellTreelist,
    },
    {
      // @ts-ignore
      field: 'date',
      title: t('DOCUMENTS_TABLE.COLUMN_EFFECTIVE_DATE'),
      className: 'tableCell',
      headerCell: HeaderCell,
      cell: DateCellTreelist,
    },
    {
      // @ts-ignore
      field: 'isRead',
      title: t('DOCUMENTS_TABLE.COLUMN_READ'),
      className: 'tableCell',
      headerCell: HeaderCell,
      cell: ReadDocCellTreelist,
    },
    {
      // @ts-ignore
      field: 'actions',
      title: t('DOCUMENTS_TABLE.COLUMN_ACTIONS'),
      headerCell: (props) => <HeaderCell {...props} multipleDownload={isMultipleDownloadOn} onDownloadClick={handleDownloadClick} />,
      cell: ActCellTreelist,
      sortable: false,
    },
  ];

  const fetchDocuments = (startResult, includeCount, sortField?, sortDir?) => {
    if (isFetching.current) return Promise.resolve();

    isFetching.current = true;
    setLoading(true);

    const start = Math.max(startResult - pageSize, 0);

    return filterDocuments(
      filterCtx.docName,
      filterCtx.funds,
      filterCtx.investors,
      filterCtx.classifications,
      filterCtx.read,
      filterCtx.fromDate,
      filterCtx.toDate,
      start,
      take,
      sortField,
      sortDir,
      filterCtx.publishChecked,
      filterCtx.effectiveChecked,
      null,
      filterCtx.period,
      includeCount
    )
      .then((results) => {
        generateDocuments(results, start, includeCount);
      })
      .catch((err) => {
        console.log('ERROR: ', err);
      })
      .finally(() => {
        isFetching.current = false;
        setLoading(false);
      });
  };

  const generateDocuments = (results, startResult, includeCount): any[] => {
    let totalCount = includeCount ? results.totalCount : total;

    const docs = documents.length === totalCount ? [...documents] : new Array(totalCount).fill({});

    results.data.forEach((doc, i) => {
      docs[i + startResult] = doc;
    });

    if (includeCount) setTotal(results.totalCount);
    setDocuments(docs);
    return docs;
  };

  const fetchRequired = (startResult) => {
    for (let i = startResult; i < startResult + pageSize && i < documents.length; i++) {
      if (!documents[i].id) {
        fetchDocuments(startResult, false, sort[0].field, sort[0].dir);
        break;
      }
    }
  };

  const handlePageChange = (event) => {
    let start = !event.page.skip || Number.isNaN(event.page.skip) ? 0 : event.page.skip;
    fetchRequired(start);
    setSkip(start);
  };

  const handleSortChange = (e) => {
    setSort(e.sort);
    documents.fill({});
    fetchDocuments(skip, false, e.sort[0].field, e.sort[0].dir);
  };

  const onExpandChange = (e): void => {
    const clickedNode: DocumentSliceNode = new DocumentSliceNode(e.dataItem.id, e.dataItem.name, e.dataItem.type, e.dataItem.parent);
    const ancestorsNodes: DocumentSliceNode[] = clickedNode.ancestors(clickedNode.children);

    if (sliceArray.length === 1 && sliceArray[0] === 'period') {
      let periodData = clickedNode.id.split('_');
      let periodNumber = periodData[0];
      let year = periodData[1];

      const range = getPeriodRange(+year, +periodNumber);

      filterDocuments(
        filterCtx.docName,
        filterCtx.funds,
        filterCtx.investors,
        filterCtx.classifications,
        filterCtx.read,
        filterCtx.fromDate,
        filterCtx.toDate,
        Math.max(0 - pageSize, 0),
        take,
        sort[0].field,
        sort[0].dir,
        filterCtx.publishChecked,
        filterCtx.effectiveChecked,
        null,
        range,
        true
      ).then((res) => {
        docHierarchyRef.current.addChildren(res.data, sliceArray, clickedNode, docHierarchyRef.current._nodes);
        setExpanded(e.value ? expanded.filter((id) => id !== e.dataItem.nodeId) : [...expanded, e.dataItem.nodeId]);
      });
    } else {
      let formattedAncestors = [
        {
          objectId: clickedNode.id,
          type: clickedNode.type,
        },
      ];

      ancestorsNodes.forEach((ancestor) => {
        formattedAncestors.push({ objectId: ancestor.id, type: ancestor.type });
      });

      formattedAncestors = formattedAncestors.filter((ancestor) => {
        return ancestor.type !== 'period';
      });
      let periodData = [];
      let range = {};
      if (clickedNode.type === 'period') {
        periodData = clickedNode.id.split('_');
        let periodNumber = periodData[0];
        let year = periodData[1];
        range = getPeriodRange(+year, +periodNumber);
      }

      getDocsForSlice(
        formattedAncestors,
        filterCtx.docName,
        filterCtx.funds,
        filterCtx.investors,
        filterCtx.classifications,
        filterCtx.read,
        filterCtx.fromDate,
        filterCtx.toDate,
        Math.max(0 - pageSize, 0),
        take,
        sort[0].field,
        sort[0].dir,
        filterCtx.publishChecked,
        filterCtx.effectiveChecked,
        null,
        clickedNode.type === 'period' ? range : filterCtx.period,
        true
      ).then((res) => {
        docHierarchyRef.current.addChildren(res.data.data, sliceArray, clickedNode, docHierarchyRef.current._nodes);
        setExpanded(e.value ? expanded.filter((id) => id !== e.dataItem.nodeId) : [...expanded, e.dataItem.nodeId]);
      });
    }
  };

  const addExpandField = (dataTree: any[]): any => {
    return mapTree(dataTree, subItemsField, (item) =>
      extendDataItem(item, subItemsField, {
        [expandField]: expanded.includes(item.nodeId),
      })
    );
  };

  const processData = (): any => {
    return hierarchyData ? addExpandField(hierarchyData) : [];
  };

  const numberOfIdSplits = (id: string) => {
    let fields = id.split('_');
    fields = fields.slice(1);
    return fields.length;
  };

  useEffect(() => {
    if (filterCtx.slicerChoices.length === 0) {
      fetchDocuments(0, true, sort[0].field, sort[0].dir);
    }

    setExpanded((prevState) => {
      return prevState.filter((id) => {
        return filterCtx.slicerChoices.includes('period') ? numberOfIdSplits(id) === sliceArray.length : numberOfIdSplits(id) !== sliceArray.length;
      });
    });
  }, [filterCtx, impersonate]);

  useEffect(() => {
    const sliceArray = filterCtx.slicerChoices.map((item) => {
      if (item === 'fundName') {
        return 'fund';
      } else if (item === 'objectName') {
        return 'investor';
      } else if (item === 'classifications') {
        return 'classification';
      } else if (item === 'period') {
        return 'period';
      } else return null;
    });

    setLoading(true);

    setSliceArray(sliceArray);

    if (filterCtx.slicerChoices.length > 0) {
      getSlices(
        sliceArray,
        filterCtx.docName,
        filterCtx.funds,
        filterCtx.investors,
        filterCtx.classifications,
        filterCtx.read,
        filterCtx.fromDate,
        filterCtx.toDate,
        filterCtx.period,
        filterCtx.publishChecked,
        filterCtx.effectiveChecked
      )
        .then((res) => {
          setHierarchyData(docHierarchyRef.current.generate(res.data, sliceArray));
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [filterCtx.slicerChoices]);

  const isSlicerOn = filterCtx.initialGroup.length !== 0;

  return (
    <>
      <DocumentFilters className='hidden-mobile'/>
      <div className="documents-table hidden-desktop">
        <div className="filters mobile-document-name-filter">
          <DocNameFilter
            table="documents"
          />
        </div>
        <Grid
          data={documents.slice(skip, skip + pageSize)}
          pageSize={pageSize}
          skip={skip}
          total={total}
          scrollable="virtual"
          sortable={{ allowUnsort: false }}
          sort={sort}
          rowHeight={40}
          dataItemKey={'id'}
          ref={tableRef}
          onPageChange={handlePageChange}
          onSortChange={handleSortChange}
        >
          <GridColumn
            field="label"
            title={t('DOCUMENTS_TABLE.COLUMN_NAME')}
            headerCell={HeaderCell}
            className="tableCell"
            cell={GridMobileNameCell}
          />
          <GridColumn
            field="classifications"
            title={t('DOCUMENTS_TABLE.COLUMN_CLASSIFICATIONS')}
            headerCell={HeaderCell}
            className="tableCell"
            cell={ChipsCell}
            sortable={false}
            minResizableWidth={180}
          />
          <GridColumn
            field="fundName"
            title={t('DOCUMENTS_TABLE.COLUMN_FUND')}
            headerCell={HeaderCell}
            className="tableCell"
            cell={TextCellDC}
            minResizableWidth={180}
          />
        </Grid>
      </div>
      <div className="documents-table hidden-mobile">
        {isSlicerOn ? (
          <TreeList
            data={orderBy(processData(), sliceSort)}
            columns={columns}
            className={'documents-table-treelist'}
            subItemsField={subItemsField}
            expandField={expandField}
            onExpandChange={onExpandChange}
            sortable={true}
            sort={sliceSort}
            onSortChange={(sortEvent: TreeListSortChangeEvent) => {
              setSliceSort(sortEvent.sort);
            }}
            ref={tableRef}
          />
        ) : (
          <Grid
            data={documents.slice(skip, skip + pageSize)}
            pageSize={pageSize}
            skip={skip}
            total={total}
            scrollable="virtual"
            sortable={{ allowUnsort: false }}
            sort={sort}
            rowHeight={40}
            dataItemKey={'id'}
            resizable={true}
            ref={tableRef}
            onPageChange={handlePageChange}
            onSortChange={handleSortChange}>
            <GridColumn
              field="label"
              title={t('DOCUMENTS_TABLE.COLUMN_NAME')}
              headerCell={HeaderCell}
              className="tableCell"
              cell={DocNameCell}
              minResizableWidth={180}
            />
            <GridColumn
              field="publishDate"
              title={t('DOCUMENTS_TABLE.COLUMN_POSTED')}
              headerCell={HeaderCell}
              cell={DateCell}
              width={150}
              minResizableWidth={120}
            />
            <GridColumn
              field="classifications"
              title={t('DOCUMENTS_TABLE.COLUMN_CLASSIFICATIONS')}
              headerCell={HeaderCell}
              className="tableCell"
              cell={(props) => <ChipsCell useTooltip={true} {...props}/>}
              sortable={false}
              minResizableWidth={180}
            />
            <GridColumn
              field="fundName"
              title={t('DOCUMENTS_TABLE.COLUMN_FUND')}
              headerCell={HeaderCell}
              className="tableCell"
              cell={TextCellDC}
              minResizableWidth={180}
            />
            <GridColumn
              field="fundInvestorName"
              title={t('DOCUMENTS_TABLE.COLUMN_INVESTOR')}
              headerCell={HeaderCell}
              className="tableCell"
              cell={TextCellDC}
              minResizableWidth={180}
            />
            <GridColumn
              field="date"
              title={t('DOCUMENTS_TABLE.COLUMN_EFFECTIVE_DATE')}
              headerCell={HeaderCell}
              cell={DateCell}
              width={150}
              minResizableWidth={120}
            />
            <GridColumn
              field="isRead"
              title={t('DOCUMENTS_TABLE.COLUMN_READ')}
              headerCell={HeaderCell}
              className="tableCell"
              cell={ReadDocCell}
              width={100}
              minResizableWidth={100}
            />
            <GridColumn
              field="actions"
              title={t('DOCUMENTS_TABLE.COLUMN_ACTIONS')}
              headerCell={(props) => <HeaderCell {...props} multipleDownload={isMultipleDownloadOn} onDownloadClick={handleDownloadClick} />}
              cell={ActCell}
              width={130}
              sortable={false}
            />
          </Grid>
        )}
        {loading && <TableLoading tableElement={tableRef} />}
      </div>
    </>
  );
};

export default DocumentsTable;
